<template>
  <!-- Skip Links para accesibilidad -->
  <div class="skip-links">
    <a href="#main-content" class="skip-link">
      Saltar al contenido principal
    </a>
    <a href="#sidenav-main" class="skip-link">
      Saltar al menú de navegación
    </a>
    <a href="#navbar-actions" class="skip-link">
      Saltar a las acciones del navbar
    </a>
    <a href="#footer-main" class="skip-link">
      Saltar al pie de página
    </a>
  </div>

  <!-- Live regions para anuncios de accesibilidad -->
  <div id="accessibility-announcements" aria-live="polite" aria-atomic="true" class="sr-only">
    {{ currentAnnouncement }}
  </div>
  
  <div id="accessibility-alerts" aria-live="assertive" aria-atomic="true" class="sr-only">
    {{ currentAlert }}
  </div>

  <nav
    class="shadow-none navbar navbar-main navbar-expand-lg border-radius-xl liquid-glass"
    v-bind="$attrs"
    id="navbarBlur"
    role="banner"
    aria-label="Barra de navegación principal"
    data-scroll="true"
  >
    <div class="px-3 py-1 container-fluid" style="overflow: visible !important;">
      <div class="d-flex align-items-center">
        <!-- Botón de toggle Sidenav al lado de los breadcrumbs -->
        <a
          href="#"
          class="p-0 nav-link me-3"
          :class="textWhite ? textWhite : 'text-body'"
          @click.prevent="toggleFullSidenav"
          data-toggle-sidebar
          aria-label="Alternar menú lateral"
          :aria-expanded="sidenavOpen"
        >
          <i :class="[sidenavOpen ? 'ri-menu-fold-line' : 'ri-menu-unfold-line', 'fs-26']"></i>
        </a>

        <!-- Dynamic Breadcrumbs Inteligentes -->
        <nav 
          class="breadcrumb-container d-none d-lg-block" 
          aria-label="breadcrumb"
          :class="{ 'loading': isBreadcrumbsLoading }"
        >
          <div v-if="isBreadcrumbsLoading" class="breadcrumb-skeleton">
            <div class="skeleton-item"></div>
            <div class="skeleton-separator"></div>
            <div class="skeleton-item"></div>
            <div class="skeleton-separator"></div>
            <div class="skeleton-item"></div>
          </div>
          
          <ol v-else class="breadcrumb animated-breadcrumb mb-0">
            <li 
              v-for="(crumb, index) in breadcrumbs" 
              :key="`${crumb.path}-${index}`"
              class="breadcrumb-item"
              :class="{ 
                'active': index === breadcrumbs.length - 1,
                'home': crumb.isHome,
                'id-segment': crumb.isId,
                'uuid-segment': crumb.isUuid,
                'date-segment': crumb.isDate,
                'generated': crumb.isGenerated
              }"
            >
              <!-- Separador inteligente -->
              <i 
                v-if="index > 0" 
                class="breadcrumb-separator ri-arrow-right-s-line"
                aria-hidden="true"
              ></i>
              
              <!-- Link navegable -->
              <button
                v-if="index < breadcrumbs.length - 1"
                @click="handleBreadcrumbNavigation(crumb)"
                class="breadcrumb-link btn-link"
                :class="textWhite ? textWhite : 'text-body'"
                :aria-label="`Navegar a ${crumb.name}`"
                type="button"
              >
                <i 
                  v-if="crumb.icon" 
                  :class="crumb.icon" 
                  class="breadcrumb-icon"
                  aria-hidden="true"
                ></i>
                <span class="breadcrumb-text">{{ crumb.name }}</span>
                
                <!-- Badge para elementos especiales -->
                <span 
                  v-if="crumb.isId" 
                  class="breadcrumb-badge id-badge"
                  title="Identificador"
                >
                  ID
                </span>
                <span 
                  v-else-if="crumb.isUuid" 
                  class="breadcrumb-badge uuid-badge"
                  title="UUID"
                >
                  UUID
                </span>
                <span 
                  v-else-if="crumb.isDate" 
                  class="breadcrumb-badge date-badge"
                  title="Fecha"
                >
                  <i class="ri-calendar-line"></i>
                </span>
              </button>
              
              <!-- Elemento actual (no navegable) -->
              <span 
                v-else 
                class="breadcrumb-current"
                :class="textWhite ? textWhite : 'text-dark'"
              >
                <i 
                  v-if="crumb.icon" 
                  :class="crumb.icon" 
                  class="breadcrumb-icon current-icon"
                  aria-hidden="true"
                ></i>
                <span class="breadcrumb-text current-text">{{ crumb.name }}</span>
                
                <!-- Indicador de página actual -->
                <span 
                  class="current-indicator"
                  aria-label="Página actual"
                >
                  <i class="ri-check-line"></i>
                </span>
              </span>
            </li>
          </ol>
          
          <!-- Botón para mostrar breadcrumbs completos en móvil -->
          <button 
            class="breadcrumb-mobile-toggle d-lg-none"
            @click="toggleMobileBreadcrumbs"
            :aria-expanded="showMobileBreadcrumbs"
            aria-label="Mostrar ruta completa"
          >
            <i class="ri-map-pin-line"></i>
            <span>{{ breadcrumbs[breadcrumbs.length - 1]?.name || 'Ubicación' }}</span>
          </button>
        </nav>
        
        <!-- Breadcrumbs móviles -->
        <div 
          v-if="showMobileBreadcrumbs"
          class="mobile-breadcrumbs d-lg-none"
          role="dialog"
          aria-label="Ruta de navegación completa"
          @click="handleMobileBreadcrumbsOutsideClick"
        >
          <div class="mobile-breadcrumb-header">
            <h6>Ruta de navegación</h6>
            <button 
              @click="closeMobileBreadcrumbs"
              aria-label="Cerrar"
            >
              <i class="ri-close-line"></i>
            </button>
          </div>
          <div class="mobile-breadcrumb-list">
            <div 
              v-for="(crumb, index) in breadcrumbs" 
              :key="`mobile-${crumb.path}-${index}`"
              class="mobile-breadcrumb-item"
              @click="handleBreadcrumbNavigation(crumb); closeMobileBreadcrumbs()"
            >
              <i v-if="crumb.icon" :class="crumb.icon"></i>
              <span>{{ crumb.name }}</span>
              <i v-if="index === breadcrumbs.length - 1" class="ri-check-line current-mark"></i>
            </div>
          </div>
        </div>
      </div>
      <div
        class="mt-2 collapse navbar-collapse mt-sm-0 me-md-0 me-sm-4"
        :class="$store.state.isRTL ? 'px-0' : 'me-sm-4'"
        id="navbar"
        style="overflow: visible !important;"
      >
        <!-- Spacer to maintain right-side positioning -->
        <div class="flex-grow-1"></div>
        
        <ul 
          class="navbar-nav justify-content-end" 
          id="navbar-actions"
          role="menubar"
          aria-label="Acciones del usuario"
          style="overflow: visible !important;"
        >
          <!-- Pomodoro Compacto -->
          <li class="nav-item d-flex align-items-center me-3" @click.stop>
            <PomodoroNavbar />
          </li>
          
          <!-- Auth Button (solo para usuarios no logueados) -->
          <li class="nav-item d-flex align-items-center" v-if="!loggedIn">
            <router-link
              :to="{ name: 'Login' }"
              class="px-0 nav-link font-weight-bold"
              :class="textWhite ? textWhite : 'text-body'"
            >
              <i
                class="fa fa-user"
                :class="$store.state.isRTL ? 'ms-sm-2' : 'me-sm-1'"
              ></i>
              <span v-if="$store.state.isRTL" class="d-sm-inline d-none">
                يسجل دخول
              </span>
              <span v-else class="d-sm-inline d-none">Sign In </span>
            </router-link>
          </li>

          <!-- Notifications -->
          <li
            class="nav-item dropdown d-flex align-items-center position-static"
            :class="$store.state.isRTL ? 'me-3' : 'ms-3'"
            v-click-outside="closeNotifications"
            style="z-index: 99999 !important;"
          >
            <a
              href="#"
              class="nav-link position-relative notification-container"
              :class="textWhite ? textWhite : 'text-body'"
              id="notificationsDropdown"
              @click.prevent="toggleNotifications"
              title="Notificaciones"
              :aria-label="'Notificaciones. ' + (unreadNotifications > 0 ? unreadNotifications + ' no leídas' : 'No hay notificaciones nuevas')"
              role="button"
            >
              <i class="ri-notification-3-line navbar-icon-large"></i>
              <span
                v-if="unreadNotifications > 0"
                class="notification-badge"
                :class="{ 'pulse': hasNewNotifications }"
              >
                {{ unreadNotifications > 99 ? '99+' : unreadNotifications }}
              </span>
            </a>
            <ul
              class="dropdown-menu dropdown-menu-end notifications-dropdown px-0 py-2"
              :class="{ 'show': showNotifications }"
              aria-labelledby="notificationsDropdown"
            >
              <li class="dropdown-header d-flex justify-content-between align-items-center px-3 py-2">
                <h6 class="mb-0 fw-bold">Notificaciones</h6>
                <button
                  v-if="unreadNotifications > 0"
                  @click="markAllAsRead"
                  class="btn btn-sm btn-link p-0 text-primary"
                >
                  Marcar todas como leídas
                </button>
              </li>
              <li><hr class="dropdown-divider my-1"></li>
              
              <div class="notifications-list" style="max-height: 350px; overflow-y: auto;">
                <li
                  v-for="notification in notifications"
                  :key="notification.id"
                  class="notification-item"
                  :class="{ 'unread': !notification.read }"
                  @click="markAsRead(notification.id)"
                >
                  <div class="notification-content">
                    <div class="notification-icon" :class="getNotificationIconClass(notification.type)">
                      <i :class="getNotificationIcon(notification.type)"></i>
                    </div>
                    <div class="notification-text">
                      <h6 class="notification-title">{{ notification.title }}</h6>
                      <p class="notification-message">{{ notification.message }}</p>
                      <small class="notification-time">{{ formatTime(notification.timestamp) }}</small>
                    </div>
                    <div v-if="!notification.read" class="unread-indicator"></div>
                  </div>
                </li>
                
                <li v-if="notifications.length === 0" class="text-center py-4 text-muted">
                  <i class="ri-notification-off-line fs-24 mb-2"></i>
                  <p class="mb-0">No hay notificaciones</p>
                </li>
              </div>
              
              <li><hr class="dropdown-divider my-1"></li>
              <li class="text-center py-2">
                <button class="btn btn-sm btn-primary disabled-notifications-btn" @click.prevent>
                  Ver todas las notificaciones
                </button>
              </li>
            </ul>
          </li>

          <!-- Apps Drawer Dropdown -->
          <li
            class="nav-item dropdown d-flex align-items-center position-static"
            :class="$store.state.isRTL ? 'me-3' : 'ms-3'"
            v-click-outside="closeAppsDrawer"
            style="z-index: 99998 !important;"
          >
            <a
              href="#"
              class="p-0 nav-link position-relative"
              :class="textWhite ? textWhite : 'text-body'"
              id="appsDropdownButton"
              @click.prevent="toggleAppsDrawer"
              title="Aplicaciones"
              aria-label="Menú de aplicaciones"
              role="button"
            >
              <i class="ri-apps-2-add-line navbar-icon-large"></i>
            </a>
            <div
              class="apps-dropdown-menu"
              :class="{ 'show': showAppsDrawer }"
              aria-labelledby="appsDropdownButton"
            >
              <div class="apps-header">
                <h6 class="mb-0 fw-bold text-center">Aplicaciones</h6>
                <p class="text-muted text-center mb-3 small">Accede rápidamente a tus herramientas</p>
              </div>
              
              <!-- Apps Grid -->
              <div class="apps-grid">
                <div 
                  v-for="app in appsList" 
                  :key="app.key"
                  class="app-item"
                  @click="openApp(app.key)"
                  @keydown.enter="openApp(app.key)"
                  @keydown.space.prevent="openApp(app.key)"
                  tabindex="0"
                  :aria-label="'Abrir ' + app.label"
                  role="button"
                >
                  <div class="app-icon-container">
                    <i :class="app.icon" class="app-icon"></i>
                    <div class="app-icon-glow"></div>
                  </div>
                  <span class="app-name">{{ app.label }}</span>
                </div>
              </div>
              
              <div v-if="additionalApps.length > 0" class="apps-divider"></div>
              
              <!-- Additional Apps Grid -->
              <div v-if="additionalApps.length > 0" class="apps-grid additional-apps">
                <div 
                  v-for="app in additionalApps" 
                  :key="app.key"
                  class="app-item"
                  @click="openApp(app.key)"
                  @keydown.enter="openApp(app.key)"
                  @keydown.space.prevent="openApp(app.key)"
                  tabindex="0"
                  :aria-label="'Abrir ' + app.label"
                  role="button"
                >
                  <div class="app-icon-container">
                    <i :class="app.icon" class="app-icon"></i>
                    <div class="app-icon-glow"></div>
                  </div>
                  <span class="app-name">{{ app.label }}</span>
                </div>
              </div>
            </div>
          </li>

          <!-- Dark Mode Toggle - Rehabilitado con versión optimizada -->
          <li
            class="nav-item d-flex align-items-center"
            :class="$store.state.isRTL ? 'me-3' : 'ms-3'"
          >
            <a
              href="#"
              class="p-0 nav-link"
              :class="textWhite ? textWhite : 'text-body'"
              @click.prevent="toggleDarkMode"
              :title="themeText"
              :aria-label="themeText"
              role="button"
            >
              <i 
                :class="[themeIcon, 'moon-icon', 'navbar-icon-large']"
                style="transition: all 0.3s ease;"
              ></i>
            </a>
          </li>

          <!-- User Section -->
          <li 
            class="nav-item dropdown d-flex align-items-center position-static"
            :class="$store.state.isRTL ? 'me-3' : 'ms-3'"
            v-if="isUserLoggedIn"
            v-click-outside="closeUserDropdown"
            style="z-index: 99997 !important;"
          >
            <a
              href="#"
              class="p-0 nav-link dropdown-toggle user-dropdown-toggle"
              :class="textWhite ? textWhite : 'text-body'"
              role="button"
              :aria-expanded="showUserDropdown"
              :aria-label="'Menú de ' + user.name"
              @click.prevent="toggleUserDropdown"
            >
              <div class="user-info-navbar">
                <!-- Avatar del usuario -->
                <div class="avatar-navbar">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    :alt="user.name"
                    class="avatar-image-navbar"
                  />
                  <div
                    v-else
                    class="avatar-initials-navbar"
                  >
                    {{ userInitials }}
                  </div>
                </div>
                
                <!-- Información del usuario (oculta en móvil) -->
                <div class="user-details-navbar d-none d-md-block">
                  <p class="user-name-navbar mb-0">
                    {{ user.name || 'Usuario' }}
                  </p>
                  <p class="user-role-navbar mb-0">
                    {{ user.role || 'Usuario' }}
                  </p>
                </div>
              </div>
            </a>
            
            <!-- Dropdown Menu -->
            <ul 
              class="dropdown-menu dropdown-menu-end user-dropdown-menu" 
              :class="{ 'show': showUserDropdown }"
              v-show="showUserDropdown"
            >
              <li class="dropdown-item-text user-info-dropdown">
                <div class="d-flex align-items-center">
                  <div class="avatar-dropdown me-3">
                    <img
                      v-if="user.avatar"
                      :src="user.avatar"
                      :alt="user.name"
                      class="avatar-image-dropdown"
                    />
                    <div
                      v-else
                      class="avatar-initials-dropdown"
                    >
                      {{ userInitials }}
                    </div>
                  </div>
                  <div class="user-details-dropdown">
                    <p class="user-name-dropdown mb-1">{{ user.name || 'Usuario' }}</p>
                    <p class="user-role-dropdown mb-0">{{ user.role || 'Usuario' }}</p>
                  </div>
                </div>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li>
                <a class="dropdown-item" href="#" @click.prevent="logoutUser">
                  <i class="ri-logout-box-line me-2"></i>
                  Cerrar Sesión
                </a>
              </li>
            </ul>
          </li>

          <!-- Mobile Sidebar Toggle -->
          <li class="nav-item d-xl-none ps-3 d-flex align-items-center">
            <a
              href="#"
              @click="toggleSidebar"
              class="p-0 nav-link text-body"
              id="iconNavbarSidenav"
            >
              <div class="sidenav-toggler-inner">
                <i class="sidenav-toggler-line"></i>
                <i class="sidenav-toggler-line"></i>
                <i class="sidenav-toggler-line"></i>
              </div>
            </a>
          </li>

        </ul>
      </div>
    </div>
  </nav>

  <!-- Modal de ayuda de keyboard shortcuts -->
  <div 
    v-if="showShortcutsModal" 
    class="shortcuts-modal-overlay"
    @click="closeShortcutsModal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="shortcuts-modal-title"
  >
    <div 
      class="shortcuts-modal"
      @click.stop
      tabindex="-1"
      ref="shortcutsModal"
    >
      <div class="shortcuts-modal-header">
        <h3 id="shortcuts-modal-title" class="shortcuts-modal-title">
          <i class="ri-keyboard-line me-2"></i>
          Atajos de Teclado
        </h3>
        <button 
          @click="closeShortcutsModal"
          class="shortcuts-modal-close"
          aria-label="Cerrar modal de atajos"
        >
          <i class="ri-close-line"></i>
        </button>
      </div>
      
      <div class="shortcuts-modal-body">
        <div class="shortcuts-section">
          <h4 class="shortcuts-section-title">
            <i class="ri-navigation-line me-2"></i>
            Navegación
          </h4>
          <div class="shortcuts-grid">
            <div class="shortcut-item">
              <div class="shortcut-keys">
                <kbd>Alt</kbd> + <kbd>1</kbd>
              </div>
              <div class="shortcut-description">Ir al contenido principal</div>
            </div>
            <div class="shortcut-item">
              <div class="shortcut-keys">
                <kbd>Alt</kbd> + <kbd>2</kbd>
              </div>
              <div class="shortcut-description">Ir al menú lateral</div>
            </div>
            <div class="shortcut-item">
              <div class="shortcut-keys">
                <kbd>Alt</kbd> + <kbd>H</kbd>
              </div>
              <div class="shortcut-description">Ir al dashboard</div>
            </div>
          </div>
        </div>
        
        <div class="shortcuts-section">
          <h4 class="shortcuts-section-title">
            <i class="ri-settings-3-line me-2"></i>
            Interfaz
          </h4>
          <div class="shortcuts-grid">
            <div class="shortcut-item">
              <div class="shortcut-keys">
                <kbd>Ctrl</kbd> + <kbd>B</kbd>
              </div>
              <div class="shortcut-description">Alternar menú lateral</div>
            </div>
            <div class="shortcut-item">
              <div class="shortcut-keys">
                <kbd>Ctrl</kbd> + <kbd>K</kbd>
              </div>
              <div class="shortcut-description">Búsqueda global (próximamente)</div>
            </div>
            <div class="shortcut-item">
              <div class="shortcut-keys">
                <kbd>Esc</kbd>
              </div>
              <div class="shortcut-description">Cerrar menús y modales</div>
            </div>
          </div>
        </div>
        
        <div class="shortcuts-section">
          <h4 class="shortcuts-section-title">
            <i class="ri-user-settings-line me-2"></i>
            Usuario
          </h4>
          <div class="shortcuts-grid">
            <div class="shortcut-item">
              <div class="shortcut-keys">
                <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>L</kbd>
              </div>
              <div class="shortcut-description">Cerrar sesión</div>
            </div>
          </div>
        </div>
        
        <div class="shortcuts-section">
          <h4 class="shortcuts-section-title">
            <i class="ri-question-line me-2"></i>
            Ayuda
          </h4>
          <div class="shortcuts-grid">
            <div class="shortcut-item">
              <div class="shortcut-keys">
                <kbd>Ctrl</kbd> + <kbd>/</kbd>
              </div>
              <div class="shortcut-description">Mostrar este modal</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="shortcuts-modal-footer">
        <button 
          @click="closeShortcutsModal"
          class="btn btn-primary"
        >
          Entendido
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState, mapGetters } from "vuex";
import { useDarkModeSafe } from "@/composables/useDarkModeSafe"; // Rehabilitado con versión optimizada
import { useKeyboardShortcuts } from "@/composables/useKeyboardShortcuts";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import PomodoroNavbar from "@/components/PomodoroNavbar.vue";

export default {
  name: "Navbar",
  components: {
    PomodoroNavbar,
  },
  
  props: {
    minNav: Boolean,
    textWhite: String,
  },
  
  setup() {
    const { isDark, toggleDarkMode, initializeDarkMode, themeIcon, themeText, cleanup } = useDarkModeSafe(); // Rehabilitado con versión optimizada
    
    // Configurar breadcrumbs inteligentes
    const { 
      breadcrumbs: intelligentBreadcrumbs, 
      isLoading: breadcrumbsLoading,
      registerCustomRoutes,
      navigateToBreadcrumb
    } = useBreadcrumbs({
      enableCache: true,
      enableAnimations: true,
      showIcons: true,
      homeRoute: { name: 'Dashboard', path: '/dashboard' },
      debug: process.env.NODE_ENV === 'development'
    });
    
    // Registrar rutas personalizadas específicas de la aplicación
    registerCustomRoutes({
      'actualizacion-masiva': { 
        name: 'Actualización Masiva', 
        icon: 'ri-refresh-line' 
      },
      'conectividad': { 
        name: 'CON - Conectividad', 
        icon: 'ri-wifi-line' 
      },
      'borrado-registros': { 
        name: 'Borrado de Registros', 
        icon: 'ri-delete-bin-line' 
      },
      'localizaciones': { 
        name: 'Localizaciones Celsig', 
        icon: 'ri-map-pin-line' 
      },
      'celsig5': { 
        name: 'CelSig5 y SCeGR', 
        icon: 'ri-settings-3-line' 
      },
      'billing': { 
        name: 'Cargas Masivas', 
        icon: 'ri-upload-line' 
      }
    });
    
    // Configurar keyboard shortcuts
    const shortcuts = {
      'ctrl+k': () => {
        // Futuro: abrir búsqueda global
        console.log('Búsqueda global (próximamente)')
      },
      'ctrl+b': () => {
        // Toggle sidebar
        document.querySelector('[data-toggle-sidebar]')?.click()
      },
      'ctrl+/': () => {
        // Mostrar ayuda de shortcuts
        document.dispatchEvent(new CustomEvent('openShortcutsModal'))
      },
      'esc': () => {
        // Cerrar dropdowns abiertos
        document.dispatchEvent(new Event('closeAllDropdowns'))
      },
      'ctrl+shift+l': () => {
        // Logout rápido
        if (document.querySelector('[data-logout-btn]')) {
          document.querySelector('[data-logout-btn]').click()
        }
      },
      'alt+1': () => {
        // Saltar al contenido principal
        document.querySelector('#main-content')?.focus()
      },
      'alt+2': () => {
        // Saltar al sidenav
        document.querySelector('#sidenav-main')?.focus()
      },
      'alt+h': () => {
        // Ir al dashboard
        window.location.hash = '#/dashboard'
      }
    }
    
    const { registerShortcuts } = useKeyboardShortcuts(shortcuts, {
      enableInInputs: false,
      showNotification: true,
      debug: process.env.NODE_ENV === 'development'
    })
    
    return {
      isDark, // Rehabilitado con versión optimizada
      toggleDarkMode, // Rehabilitado con versión optimizada
      initializeDarkMode, // Rehabilitado con versión optimizada
      themeIcon, // Rehabilitado con versión optimizada
      themeText, // Rehabilitado con versión optimizada
      cleanup, // Nuevo método de cleanup
      registerShortcuts,
      intelligentBreadcrumbs,
      breadcrumbsLoading,
      navigateToBreadcrumb
    };
  },

  data() {
    return {
      showAppsDrawer: false,
      showMobileBreadcrumbs: false,
      showNotifications: false,
      showUserDropdown: false,
      sidenavOpen: true,
      scrollHandler: null,
      hasNewNotifications: false,
      showShortcutsModal: false,
      currentAnnouncement: '',
      currentAlert: '',
      notifications: [
        {
          id: 1,
          type: 'success',
          title: 'Conexión exitosa',
          message: 'Se ha establecido conexión con el servidor.',
          timestamp: Date.now() - 300000,
          read: false
        }
      ],
      appsList: [
        {
          key: 'outlook',
          label: 'Outlook',
          icon: 'ri-mail-line',
          url: 'https://outlook.office.com/'
        },
        {
          key: 'sharepoint',
          label: 'SharePoint',
          icon: 'ri-share-box-line',
          url: 'https://sharepoint.microsoft.com/'
        },
        {
          key: 'powerapps',
          label: 'Power Apps',
          icon: 'ri-apps-2-line',
          url: 'https://powerapps.microsoft.com/'
        }
      ],
      additionalApps: [
        {
          key: 'planner',
          label: 'Planner',
          icon: 'ri-calendar-check-line',
          url: 'https://planner.cloud.microsoft.com/'
        },
        {
          key: 'teams',
          label: 'Microsoft Teams',
          icon: 'ri-team-line',
          url: 'https://teams.microsoft.com/'
        },
        {
          key: '4ticket',
          label: '4Ticket',
          icon: 'ri-customer-service-2-line',
          url: 'https://apps.powerapps.com/play/e/default-6b48e5df-6e8b-43c6-a6f6-873609549648/a/212baec5-0775-4612-8eb7-0b39563b02b2?tenantId=6b48e5df-6e8b-43c6-a6f6-873609549648&source=sharebutton&sourcetime=1750372405394'
        }
      ]
    };
  },

  computed: {
    ...mapState(['isNavFixed', 'isRTL']),
    ...mapGetters({
      loggedIn: 'auth/loggedIn'
    }),
    
    currentRouteName() {
      return this.$route.name;
    },
    
    defaultIconClass() {
      return this.$store.state.isRTL ? "ms-sm-2" : "me-sm-1";
    },

    // Notificaciones no leídas
    unreadNotifications() {
      return this.notifications.filter(n => !n.read).length;
    },

    // Breadcrumbs inteligentes usando el composable
    breadcrumbs() {
      return this.intelligentBreadcrumbs;
    },
    
    // Indicador de carga para breadcrumbs
    isBreadcrumbsLoading() {
      return this.breadcrumbsLoading;
    },

    // Propiedades del usuario
    user() {
      try {
        const userF = localStorage.getItem('userF');
        return userF ? JSON.parse(userF) : { name: 'Usuario', role: 'Usuario' };
      } catch (error) {
        console.warn('Error al obtener datos del usuario:', error);
        return { name: 'Usuario', role: 'Usuario' };
      }
    },

    isUserLoggedIn() {
      return this.loggedIn && localStorage.getItem('auth_token');
    },

    userInitials() {
      if (!this.user.name) return 'U';
      return this.user.name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },
  },

  directives: {
    'click-outside': {
      mounted(el, binding) {
        el._clickOutside = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },

  watch: {
    // Watch for dark mode changes - CSS variables handle the transition automatically
  },

  created() {
    this.initializeStates();
    // this.initializeDarkMode(); // Comentado para Chrome compatibility
  },

  mounted() {
    this.setupScrollListener();
    this.setupKeyboardNavigation();
    this.setupShortcutListeners();
    // Inicializar dark mode de forma optimizada
    this.initializeDarkMode();
  },

  beforeUnmount() {
    this.cleanupComponent();
  },

  methods: {
    ...mapMutations(["navbarMinimize"]),
    ...mapActions(["toggleSidebarColor"]),

    // Initialization
    initializeStates() {
      // Restore sidenav state
      const savedSidenavState = localStorage.getItem('sidenavOpen');
      if (savedSidenavState !== null) {
        this.sidenavOpen = savedSidenavState === 'true';
        if (!this.sidenavOpen) {
          this.$nextTick(() => {
            this.applySidenavCollapsedStyles(true);
          });
        }
      }
    },


    setupScrollListener() {
      const navbar = document.getElementById("navbarBlur");
      if (!navbar) return;

      this.scrollHandler = () => {
        if (window.scrollY > 10 && this.isNavFixed) {
          navbar.classList.add("blur", "position-sticky", "shadow-blur", "scrolled");
        } else {
          navbar.classList.remove("blur", "position-sticky", "shadow-blur", "scrolled");
        }
      };
      
      window.addEventListener("scroll", this.scrollHandler);
    },

    // setupMutationObserver() { // Comentado para evitar problemas en Chrome
    //   // Watch for class changes on body (dark mode toggle)
    //   this.bodyObserver = new MutationObserver((mutations) => {
    //     mutations.forEach((mutation) => {
    //       if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
    //         this.$nextTick(() => {
    //           this.forceGlassmorphism();
    //         });
    //       }
    //     });
    //   });
    //   
    //   if (document.body) {
    //     this.bodyObserver.observe(document.body, {
    //       attributes: true,
    //       attributeFilter: ['class']
    //     });
    //   }
    //   
    //   // Watch for changes on navbar itself
    //   const navbar = document.getElementById("navbarBlur");
    //   if (navbar) {
    //     this.navbarObserver = new MutationObserver((mutations) => {
    //       mutations.forEach((mutation) => {
    //         if (mutation.type === 'attributes' && (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
    //           this.$nextTick(() => {
    //             this.forceGlassmorphism();
    //           });
    //         }
    //       });
    //     });
    //     
    //     this.navbarObserver.observe(navbar, {
    //       attributes: true,
    //       attributeFilter: ['class', 'style']
    //     });
    //   }
    // },

    cleanupComponent() {
      if (this.scrollHandler) {
        window.removeEventListener("scroll", this.scrollHandler);
      }
      if (this.keyboardHandler) {
        document.removeEventListener("keydown", this.keyboardHandler);
      }
      // if (this.bodyObserver) { // Comentado para evitar problemas en Chrome
      //   this.bodyObserver.disconnect();
      // }
      // if (this.navbarObserver) { // Comentado para evitar problemas en Chrome
      //   this.navbarObserver.disconnect();
      // }
      
      // Cleanup dark mode listeners
      if (this.cleanup) {
        this.cleanup();
      }
    },

    setupKeyboardNavigation() {
      this.keyboardHandler = (event) => {
        // Global keyboard shortcuts
        if (event.ctrlKey || event.metaKey) {
          // No global keyboard shortcuts currently
        }
        
        // Escape to close dropdowns and modals
        if (event.key === 'Escape') {
          if (this.showShortcutsModal) {
            this.closeShortcutsModal();
          } else {
            this.closeAllDropdowns();
          }
        }
      };
      
      document.addEventListener("keydown", this.keyboardHandler);
    },
    
    setupShortcutListeners() {
      // Listener para abrir modal de shortcuts
      document.addEventListener('openShortcutsModal', () => {
        this.openShortcutsModal();
      });
      
      // Listener para cerrar dropdowns
      document.addEventListener('closeAllDropdowns', () => {
        this.closeAllDropdowns();
      });
    },

    closeAllDropdowns() {
      this.showNotifications = false;
      this.showAppsDrawer = false;
      this.showUserDropdown = false;
    },

    // User dropdown methods
    toggleUserDropdown() {
      this.showUserDropdown = !this.showUserDropdown;
      // Cerrar otros dropdowns cuando se abre el del usuario
      if (this.showUserDropdown) {
        this.showNotifications = false;
        this.showAppsDrawer = false;
      }
    },

    closeUserDropdown() {
      this.showUserDropdown = false;
    },

    // Sidebar methods
    toggleSidebar() {
      // Usar color consciente del tema en lugar de bg-white hardcodeado
      const sidebarColor = this.isDark ? "bg-transparent" : "bg-light";
      this.toggleSidebarColor(sidebarColor);
      this.navbarMinimize();
    },

    toggleFullSidenav() {
      const sidenav = document.getElementById("sidenav-main");
      if (!sidenav) return;

      if (this.sidenavOpen) {
        document.body.classList.add("g-sidenav-hidden");
        document.body.classList.remove("g-sidenav-show");
        sidenav.classList.add("sidenav-mini");
        this.applySidenavCollapsedStyles(true);
      } else {
        document.body.classList.remove("g-sidenav-hidden");
        document.body.classList.add("g-sidenav-show");
        sidenav.classList.remove("sidenav-mini");
        this.applySidenavCollapsedStyles(false);
      }
      
      this.sidenavOpen = !this.sidenavOpen;
      localStorage.setItem('sidenavOpen', this.sidenavOpen);
    },

    applySidenavCollapsedStyles(collapsed) {
      const sidenav = document.getElementById("sidenav-main");
      if (!sidenav) return;
      
      const elements = {
        navTexts: sidenav.querySelectorAll(".nav-link-text"),
        iconContainers: sidenav.querySelectorAll(".sidenav-icon-container, .icon"),
        svgIcons: sidenav.querySelectorAll(".sidenav-icon-container svg, .icon svg"),
        navLinks: sidenav.querySelectorAll(".nav-link, .sidenav-item"),
        navItems: sidenav.querySelectorAll(".nav-item"),
        sectionHeaders: sidenav.querySelectorAll(".section-header, h6.text-uppercase, hr"),
        logo: sidenav.querySelector(".navbar-brand img"),
        indicators: sidenav.querySelectorAll(".nav-item-indicator")
      };

      if (collapsed) {
        // Apply collapsed styles
        sidenav.style.cssText = `
          width: 78px !important;
          overflow-x: hidden !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        `;

        // Hide text elements
        elements.navTexts.forEach(text => {
          text.style.display = "none";
        });

        // Center and resize icon containers
        elements.iconContainers.forEach(container => {
          container.style.cssText = `
            margin: 0 auto !important; 
            width: 2rem !important; 
            height: 2rem !important; 
            min-height: 2rem !important;
            flex-shrink: 0 !important;
          `;
        });

        // Ensure proper icon sizing
        elements.svgIcons.forEach(icon => {
          icon.style.cssText = `
            width: 1rem !important; 
            height: 1rem !important;
            flex-shrink: 0 !important;
          `;
        });

        // Center navigation links
        elements.navLinks.forEach(link => {
          link.style.cssText = `
            justify-content: center !important; 
            padding: 0.75rem 0.5rem !important; 
            margin: 0.125rem 0.25rem !important;
            display: flex !important;
            align-items: center !important;
          `;
        });

        // Center nav items
        elements.navItems.forEach(item => {
          item.style.cssText = `
            text-align: center !important; 
            padding: 0 !important;
            display: flex !important;
            justify-content: center !important;
          `;
        });

        // Hide section headers
        elements.sectionHeaders.forEach(header => {
          header.style.display = "none";
        });

        // Hide active indicators
        elements.indicators.forEach(indicator => {
          indicator.style.display = "none";
        });

        // Resize logo
        if (elements.logo) {
          elements.logo.style.cssText = "max-width: 40px; margin: 0 auto;";
        }
      } else {
        // Restore normal styles
        sidenav.style.cssText = "";
        elements.navTexts.forEach(text => text.style.cssText = "");
        elements.iconContainers.forEach(container => container.style.cssText = "");
        elements.svgIcons.forEach(icon => icon.style.cssText = "");
        elements.navLinks.forEach(link => link.style.cssText = "");
        elements.navItems.forEach(item => item.style.cssText = "");
        elements.sectionHeaders.forEach(header => header.style.cssText = "");
        elements.indicators.forEach(indicator => indicator.style.cssText = "");
        if (elements.logo) {
          elements.logo.style.cssText = "";
        }
      }
    },



    // Apps Drawer
    toggleAppsDrawer() {
      this.showAppsDrawer = !this.showAppsDrawer;
    },

    closeAppsDrawer() {
      this.showAppsDrawer = false;
    },

    openApp(appKey) {
      const app = [...this.appsList, ...this.additionalApps].find(a => a.key === appKey);
      
      if (app && app.url && app.url !== '#') {
        window.open(app.url, '_blank');
      }
      
      this.closeAppsDrawer();
    },

    // Notifications methods
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications) {
        this.hasNewNotifications = false;
      }
    },

    closeNotifications() {
      this.showNotifications = false;
    },

    markAsRead(notificationId) {
      const notification = this.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.read = true;
      }
    },

    markAllAsRead() {
      this.notifications.forEach(n => n.read = true);
    },

    getNotificationIcon(type) {
      const icons = {
        success: 'ri-check-circle-line',
        warning: 'ri-alert-line',
        error: 'ri-error-warning-line',
        info: 'ri-information-line'
      };
      return icons[type] || 'ri-notification-line';
    },

    getNotificationIconClass(type) {
      const classes = {
        success: 'bg-success',
        warning: 'bg-warning',
        error: 'bg-danger',
        info: 'bg-info'
      };
      return classes[type] || 'bg-primary';
    },

    formatTime(timestamp) {
      const diff = Date.now() - timestamp;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Ahora';
      if (minutes < 60) return `${minutes}m`;
      if (hours < 24) return `${hours}h`;
      return `${days}d`;
    },

    // Simulate new notification
    addNotification(notification) {
      this.notifications.unshift({
        id: Date.now(),
        read: false,
        timestamp: Date.now(),
        ...notification
      });
      this.hasNewNotifications = true;
      
      // Anunciar nueva notificación
      this.announceToScreenReader(`Nueva notificación: ${notification.title}`);
      
      // Auto-remove pulse effect after 3 seconds
      setTimeout(() => {
        this.hasNewNotifications = false;
      }, 3000);
    },

    // Métodos de accesibilidad
    announceToScreenReader(message, isAlert = false) {
      if (isAlert) {
        this.currentAlert = message;
        setTimeout(() => {
          this.currentAlert = '';
        }, 3000);
      } else {
        this.currentAnnouncement = message;
        setTimeout(() => {
          this.currentAnnouncement = '';
        }, 3000);
      }
    },
    
    // Manejar eventos de keyboard shortcuts
    handleKeyboardShortcut(shortcut, action) {
      switch (action) {
        case 'toggleSidebar':
          this.toggleFullSidenav();
          this.announceToScreenReader(
            this.sidenavOpen ? 'Menú lateral abierto' : 'Menú lateral cerrado'
          );
          break;
        case 'closeDropdowns':
          this.closeAllDropdowns();
          this.announceToScreenReader('Menús cerrados');
          break;
        case 'showShortcuts':
          this.showShortcutsModal = true;
          this.announceToScreenReader('Modal de atajos de teclado abierto');
          break;
        default:
          console.log(`Shortcut ejecutado: ${shortcut}`);
      }
    },
    
    // Modal de shortcuts
    openShortcutsModal() {
      this.showShortcutsModal = true;
      this.announceToScreenReader('Modal de atajos de teclado abierto');
      
      // Focus al modal cuando se abre
      this.$nextTick(() => {
        if (this.$refs.shortcutsModal) {
          this.$refs.shortcutsModal.focus();
        }
      });
    },
    
    closeShortcutsModal() {
      this.showShortcutsModal = false;
      this.announceToScreenReader('Modal de atajos de teclado cerrado');
    },

    // Métodos de navegación para breadcrumbs inteligentes
    handleBreadcrumbNavigation(breadcrumb) {
      try {
        // Usar el método del composable si está disponible
        if (this.navigateToBreadcrumb) {
          this.navigateToBreadcrumb(breadcrumb);
        } else {
          // Fallback a navegación manual
          this.navigateToPath(breadcrumb);
        }
        
        // Anunciar navegación para accesibilidad
        this.announceToScreenReader(`Navegando a ${breadcrumb.name}`);
      } catch (error) {
        console.error('Error navegando breadcrumb:', error);
        this.announceToScreenReader('Error al navegar');
      }
    },

    navigateToPath(breadcrumb) {
      // Verificar si es una ruta válida
      if (!breadcrumb.path && !breadcrumb.route) {
        console.warn('Breadcrumb sin ruta válida:', breadcrumb);
        return;
      }

      try {
        // Navegar usando router-link si tiene objeto route
        if (breadcrumb.route && typeof breadcrumb.route === 'object') {
          this.$router.push(breadcrumb.route);
        } 
        // Navegar usando path string
        else if (breadcrumb.path) {
          this.$router.push(breadcrumb.path);
        }
      } catch (error) {
        console.error('Error en navegación:', error);
        // Fallback a navegación por hash si falla router
        if (breadcrumb.path) {
          window.location.hash = breadcrumb.path;
        }
      }
    },

    // Método para mostrar breadcrumbs móviles
    toggleMobileBreadcrumbs() {
      this.showMobileBreadcrumbs = !this.showMobileBreadcrumbs;
      
      if (this.showMobileBreadcrumbs) {
        this.announceToScreenReader('Modal de navegación abierto');
        // Prevenir scroll del body
        document.body.style.overflow = 'hidden';
      } else {
        this.announceToScreenReader('Modal de navegación cerrado');
        // Restaurar scroll del body
        document.body.style.overflow = '';
      }
    },

    // Método para cerrar breadcrumbs móviles
    closeMobileBreadcrumbs() {
      this.showMobileBreadcrumbs = false;
      document.body.style.overflow = '';
    },

    // Método para manejar clics fuera del modal móvil
    handleMobileBreadcrumbsOutsideClick(event) {
      const modal = event.target.closest('.mobile-breadcrumb-list');
      if (!modal && this.showMobileBreadcrumbs) {
        this.closeMobileBreadcrumbs();
      }
    },

    // Método para obtener el breadcrumb actual
    getCurrentBreadcrumb() {
      const breadcrumbs = this.breadcrumbs || [];
      return breadcrumbs[breadcrumbs.length - 1] || { name: 'Inicio', path: '/dashboard' };
    },

    // Método para obtener el contexto del breadcrumb (útil para analytics)
    getBreadcrumbContext() {
      return {
        current: this.getCurrentBreadcrumb(),
        path: this.breadcrumbs || [],
        route: this.$route.path,
        timestamp: Date.now()
      };
    },

    // Método para cerrar sesión
    async logoutUser() {
      try {
        const confirmLogout = await this.$confirm(
          '¿Estás seguro de que quieres cerrar sesión?',
          'Confirmar cierre de sesión',
          {
            confirmButtonText: 'Cerrar Sesión',
            cancelButtonText: 'Cancelar',
            type: 'warning'
          }
        );

        if (confirmLogout) {
          await this.$store.dispatch("auth/logout");
          this.$router.push("/login");
        }
      } catch (error) {
        if (error !== 'cancel') {
          // Handle logout error silently and redirect
          this.$router.push("/login");
        }
      }
    },

  },
};
</script>

<style scoped>
/* Estilos para la sección de usuario en el navbar */
.user-dropdown-toggle {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.user-dropdown-toggle::after {
  display: none !important;
}

.user-info-navbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  transition: all 0.3s ease;
  background: transparent;
}

.user-info-navbar:hover {
  background: rgba(23, 173, 55, 0.1);
  transform: translateY(-1px);
}

.avatar-navbar {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.user-info-navbar:hover .avatar-navbar {
  border-color: rgba(23, 173, 55, 0.3);
  transform: scale(1.05);
}

.avatar-image-navbar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-initials-navbar {
  background: linear-gradient(135deg, var(--dm-accent-primary, #17ad37) 0%, var(--dm-accent-secondary, #98ec2d) 100%);
  color: white;
  font-weight: 700;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.user-details-navbar {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-name-navbar {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--dm-text-primary, #1f2937);
  line-height: 1.2;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-role-navbar {
  font-size: 0.75rem;
  color: var(--dm-text-secondary, #6b7280);
  line-height: 1.2;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Dropdown Menu Styles */
.user-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 280px;
  border: 1px solid var(--liquid-glass-border) !important;
  border-radius: 12px;
  box-shadow: var(--liquid-shadow-xl) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  background: var(--liquid-glass-bg-hover) !important;
  padding: 0.5rem 0;
  margin-top: 0.5rem;
  z-index: 10030 !important;
  list-style: none;
  transform: translateY(-10px);
  opacity: 0;
  visibility: hidden;
  transition: var(--liquid-transition-smooth);
}

.user-dropdown-menu.show {
  transform: translateY(0) !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.user-info-dropdown {
  padding: 1rem;
  border-bottom: 1px solid rgba(23, 173, 55, 0.1);
  margin-bottom: 0.5rem;
}

.avatar-dropdown {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid rgba(23, 173, 55, 0.2);
}

.avatar-image-dropdown {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-initials-dropdown {
  background: linear-gradient(135deg, var(--dm-accent-primary, #17ad37) 0%, var(--dm-accent-secondary, #98ec2d) 100%);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.user-details-dropdown {
  flex: 1;
  min-width: 0;
}

.user-name-dropdown {
  font-size: 1rem;
  font-weight: 600;
  color: var(--dm-text-primary, #1f2937);
  line-height: 1.3;
}

.user-role-dropdown {
  font-size: 0.875rem;
  color: var(--dm-text-secondary, #6b7280);
  line-height: 1.3;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: var(--dm-text-primary, #1f2937);
  text-decoration: none;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin: 0 0.5rem;
}

.dropdown-item:hover {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  color: var(--dm-accent-primary, #17ad37);
  transform: translateX(5px);
}

.dropdown-item i {
  font-size: 1rem;
  margin-right: 0.75rem;
  transition: all 0.3s ease;
}

.dropdown-item:hover i {
  transform: scale(1.1);
}

/* Responsive */
@media (max-width: 768px) {
  .user-details-navbar {
    display: none !important;
  }
  
  .user-info-navbar {
    gap: 0;
    padding: 0.25rem;
  }
  
  .avatar-navbar {
    width: 1.75rem;
    height: 1.75rem;
  }
  
  .avatar-initials-navbar {
    font-size: 0.6875rem;
  }
  
  .user-dropdown-menu {
    min-width: 250px;
    margin-right: 1rem;
  }
}

/* Dark mode styles */
body.dark-mode-active .user-dropdown-menu {
  background: rgba(17, 24, 39, 0.95);
  border-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .user-info-dropdown {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .user-name-navbar,
body.dark-mode-active .user-name-dropdown {
  color: var(--dm-text-primary, #f9fafb);
}

body.dark-mode-active .user-role-navbar,
body.dark-mode-active .user-role-dropdown {
  color: var(--dm-text-secondary, #d1d5db);
}

body.dark-mode-active .dropdown-item {
  color: var(--dm-text-primary, #f9fafb);
}

body.dark-mode-active .dropdown-item:hover {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.2) 0%, rgba(152, 236, 45, 0.2) 100%);
  color: var(--dm-accent-primary, #17ad37);
}

body.dark-mode-active .user-info-navbar:hover {
  background: rgba(23, 173, 55, 0.2);
}

/* Navigation Styles */
.fs-26 {
  font-size: 26px !important;
}

a:hover {
  cursor: pointer;
}


/* Estilos para iconos del navbar */
.nav-link {
  position: relative;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-link:hover {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(23, 173, 55, 0.15);
}

.nav-link i {
  transition: all 0.3s ease;
}

.nav-link:hover i {
  color: #17ad37;
  transform: scale(1.1);
}

/* Theme Toggle Icon Styles */
.moon-icon {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.nav-link:hover .moon-icon {
  transform: scale(1.15) rotate(15deg);
}

/* Dark mode specific animations - commented out temporarily */
/* 
.dark-mode .moon-icon {
  animation: rotate-sun 0.5s ease-in-out;
}

@keyframes rotate-sun {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.1); }
  100% { transform: rotate(360deg) scale(1); }
}
*/

/* Sidenav Mini Styles */
.sidenav-mini {
  width: 78px !important;
  transition: width 0.3s ease, transform 0.3s ease;
  overflow-x: hidden !important;
}

.sidenav-mini::-webkit-scrollbar {
  width: 0 !important;
  display: none !important;
}

.sidenav-mini .nav-link {
  display: flex;
  justify-content: center;
}

.sidenav-mini .icon-shape {
  margin: 0 auto !important;
}

/* Transitions */
#sidenav-main, 
#sidenav-main .nav-link, 
#sidenav-main .nav-link-text,
#sidenav-main .icon-shape,
#sidenav-main h6,
#sidenav-main hr,
#sidenav-main .navbar-brand img,
#sidenav-main .sidenav-icon {
  transition: all 0.3s ease;
}

/* Notification Container */
.notification-container {
  padding: 0.5rem !important;
  min-width: 44px;
  min-height: 44px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  overflow: visible !important;
}

/* Notification Badge */
.notification-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  z-index: 15;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.9);
  line-height: 1;
}

.notification-badge.pulse {
  animation: pulse-notification 2s infinite;
}

/* Notification Icon */
.notification-container i {
  font-size: 18px !important;
  transition: all 0.3s ease;
}

.notification-container:hover i {
  color: #17ad37;
  transform: scale(1.1);
}

@keyframes pulse-notification {
  0% { transform: scale(1); box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4); }
  50% { transform: scale(1.1); box-shadow: 0 4px 16px rgba(239, 68, 68, 0.8); }
  100% { transform: scale(1); box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4); }
}

/* Notifications Dropdown */
.notifications-dropdown {
  min-width: 380px !important;
  max-width: 400px !important;
  background: var(--liquid-glass-bg-hover) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  border: 1px solid var(--liquid-glass-border) !important;
  border-radius: 1rem !important;
  box-shadow: var(--liquid-shadow-xl) !important;
  margin-top: 0.5rem !important;
  z-index: 99999 !important;
  position: absolute !important;
  top: calc(100% + 8px) !important;
  right: 0 !important;
  transform: translateY(-10px) !important;
  opacity: 0 !important;
  visibility: hidden !important;
  transition: var(--liquid-transition-smooth) !important;
  will-change: transform, opacity !important;
}

.notifications-dropdown.show {
  transform: translateY(0) !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.notification-item {
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  border-radius: 0.5rem;
  margin: 0.25rem 0.5rem;
}

.notification-item:hover {
  background: rgba(23, 173, 55, 0.05);
  transform: translateX(2px);
}

.notification-item.unread {
  background: rgba(59, 130, 246, 0.05);
  border-left: 3px solid #3b82f6;
}

.notification-content {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  position: relative;
}

.notification-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
  color: white;
  font-size: 16px;
}

.notification-text {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #374151;
  line-height: 1.2;
}

.notification-message {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 0.25rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-time {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}

.unread-indicator {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
  flex-shrink: 0;
}

.dropdown-header h6 {
  color: #374151;
  font-size: 16px;
}

.notifications-list::-webkit-scrollbar {
  width: 4px;
}

.notifications-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 2px;
}

.notifications-list::-webkit-scrollbar-thumb {
  background: rgba(23, 173, 55, 0.3);
  border-radius: 2px;
}

.notifications-list::-webkit-scrollbar-thumb:hover {
  background: rgba(23, 173, 55, 0.5);
}


/* Modern Apps Dropdown Styles */
.apps-dropdown-menu {
  position: absolute !important;
  top: 100% !important;
  right: 0 !important;
  background: var(--liquid-glass-bg-hover) !important;
  backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  -webkit-backdrop-filter: blur(var(--liquid-glass-blur-heavy)) saturate(var(--liquid-glass-saturate)) brightness(var(--liquid-glass-brightness)) !important;
  border: 1px solid var(--liquid-glass-border) !important;
  border-radius: 1.5rem !important;
  box-shadow: var(--liquid-shadow-xl) !important;
  padding: 1.5rem !important;
  margin-top: 0.75rem !important;
  z-index: 10040 !important;
  min-width: 280px !important;
  opacity: 0 !important;
  transform: translateY(-10px) scale(0.95) !important;
  transition: var(--liquid-transition-bounce) !important;
  pointer-events: none !important;
  will-change: transform, opacity !important;
}

.apps-dropdown-menu.show {
  opacity: 1 !important;
  transform: translateY(0) scale(1) !important;
  pointer-events: auto !important;
}

.apps-header {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.apps-header h6 {
  color: #2d3748;
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.apps-header p {
  font-size: 0.75rem;
  color: #718096;
  margin-bottom: 0;
}

.apps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.app-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0.5rem;
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: rgba(255, 255, 255, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.app-item:hover {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.15) 0%, rgba(152, 236, 45, 0.15) 100%);
  border-color: rgba(23, 173, 55, 0.3);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 
    0 15px 35px rgba(23, 173, 55, 0.2),
    0 5px 15px rgba(0, 0, 0, 0.1);
}

.app-item:focus {
  outline: 2px solid #17ad37;
  outline-offset: 2px;
}

.app-item:active {
  transform: translateY(-1px) scale(0.98);
}

.app-icon-container {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 1rem;
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  margin-bottom: 0.75rem;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.app-icon-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #17ad37 0%, #98ec2d 100%);
  border-radius: 1rem;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-item:hover .app-icon-container {
  background: linear-gradient(135deg, #17ad37 0%, #98ec2d 100%);
  transform: scale(1.1) rotate(5deg);
  box-shadow: 
    0 10px 25px rgba(23, 173, 55, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.app-item:hover .app-icon-glow {
  opacity: 0.2;
  transform: scale(1.2);
  animation: pulse-glow 2s infinite;
}

.app-icon {
  font-size: 1.5rem;
  color: #17ad37;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  position: relative;
}

.app-item:hover .app-icon {
  color: white;
  transform: scale(1.1);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.app-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
  text-align: center;
  line-height: 1.2;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: 100%;
  word-wrap: break-word;
}

.app-item:hover .app-name {
  color: #17ad37;
  transform: translateY(-1px);
  font-weight: 700;
}

.apps-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%);
  margin: 1rem 0;
}

.additional-apps {
  margin-top: 0.5rem;
}

@keyframes pulse-glow {
  0%, 100% {
    opacity: 0.2;
    transform: scale(1.2);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.4);
  }
}

/* Old dropdown styles for other dropdowns */
.dropdown-menu {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 1rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  margin-top: 0.5rem;
  z-index: 10003;
  position: absolute;
}

.dropdown-item {
  border-radius: 0.5rem;
  margin-bottom: 0.25rem;
  transition: all 0.3s ease;
}

.dropdown-item:hover {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  transform: translateX(3px);
}

.dropdown-item a {
  text-decoration: none;
  color: inherit;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  border-radius: 0.5rem;
  font-weight: 500;
}

.dropdown-item a:hover {
  color: #17ad37;
}

.dropdown-item a i {
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(23, 173, 55, 0.1);
  border-radius: 0.375rem;
  margin-right: 0.75rem;
  transition: all 0.3s ease;
}

.dropdown-item:hover a i {
  background: linear-gradient(135deg, #17ad37 0%, #98ec2d 100%);
  color: white;
  transform: scale(1.1);
}

.dropdown-divider {
  margin: 0.75rem 0;
  border-color: rgba(23, 173, 55, 0.2);
}

/* Enhanced Glassmorphism Effects */
.navbar {
  backdrop-filter: blur(20px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1030;
  position: relative;
}

/* Override blur class background */
.navbar.blur {
  background: transparent !important;
  backdrop-filter: blur(25px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(25px) saturate(200%) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15) !important;
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
}

/* Override shadow-blur para mantener glassmorphism */
.navbar.shadow-blur {
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.2) !important;
}

/* Modo oscuro para navbar */
body.dark-mode-active .navbar {
  background: rgba(26, 32, 44, 0.6) !important;
  backdrop-filter: blur(20px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
}

body.dark-mode-active .navbar.blur {
  background: rgba(26, 32, 44, 0.8) !important;
  backdrop-filter: blur(25px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(25px) saturate(200%) !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15) !important;
}

.navbar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(23, 173, 55, 0.02) 0%, 
    rgba(152, 236, 45, 0.02) 100%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.navbar:hover::before {
  opacity: 1;
}

/* Enhanced Animations */
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
}

@keyframes glow {
  0%, 100% { 
    box-shadow: 0 4px 15px rgba(23, 173, 55, 0.15);
  }
  50% { 
    box-shadow: 0 8px 30px rgba(23, 173, 55, 0.3);
  }
}

@keyframes shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}

.nav-link {
  position: relative;
  overflow: hidden;
}

.nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0) 0%, 
    rgba(255, 255, 255, 0.2) 50%, 
    rgba(255, 255, 255, 0) 100%);
  transition: left 0.5s ease;
  z-index: 1;
}

.nav-link:hover::before {
  left: 100%;
}


/* Micro-interactions */
.notification-badge {
  animation: pulse-notification 2s infinite;
}

.nav-link i {
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.nav-link:hover i {
  animation: float 2s ease-in-out infinite;
}


/* Enhanced Dropdown Animations */
.dropdown-menu {
  animation: dropdownSlideIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform-origin: top center;
}

@keyframes dropdownSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.dropdown-item {
  position: relative;
  overflow: hidden;
}

.dropdown-item::before {
  content: '';
  position: absolute;
  left: -100%;
  top: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    rgba(23, 173, 55, 0) 0%, 
    rgba(23, 173, 55, 0.1) 50%, 
    rgba(23, 173, 55, 0) 100%);
  transition: left 0.4s ease;
}

.dropdown-item:hover::before {
  left: 100%;
}


/* Notification Item Animations */
.notification-item {
  position: relative;
  overflow: hidden;
}

.notification-item::before {
  content: '';
  position: absolute;
  left: -100%;
  top: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    rgba(59, 130, 246, 0) 0%, 
    rgba(59, 130, 246, 0.1) 50%, 
    rgba(59, 130, 246, 0) 100%);
  transition: left 0.5s ease;
}

.notification-item:hover::before {
  left: 100%;
}

/* Breadcrumb Styles */
.breadcrumb-container {
  margin-left: 1rem;
}

.animated-breadcrumb {
  background: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  position: relative;
  animation: breadcrumbSlideIn 0.3s ease-out;
  animation-fill-mode: both;
}

.breadcrumb-item:nth-child(1) { animation-delay: 0.1s; }
.breadcrumb-item:nth-child(2) { animation-delay: 0.2s; }
.breadcrumb-item:nth-child(3) { animation-delay: 0.3s; }
.breadcrumb-item:nth-child(4) { animation-delay: 0.4s; }
.breadcrumb-item:nth-child(5) { animation-delay: 0.5s; }

@keyframes breadcrumbSlideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.breadcrumb-item:not(:last-child)::after {
  content: '';
  width: 0;
  height: 0;
  border-left: 6px solid currentColor;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  margin: 0 0.75rem;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.breadcrumb-item:hover:not(:last-child)::after {
  opacity: 0.8;
  transform: translateX(2px);
}

.breadcrumb-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 13px;
  font-weight: 500;
  position: relative;
  overflow: hidden;
}

.breadcrumb-link:hover {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(23, 173, 55, 0.15);
  color: #17ad37 !important;
}

.breadcrumb-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    rgba(23, 173, 55, 0) 0%, 
    rgba(23, 173, 55, 0.1) 50%, 
    rgba(23, 173, 55, 0) 100%);
  transition: left 0.4s ease;
}

.breadcrumb-link:hover::before {
  left: 100%;
}

.breadcrumb-current {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 13px;
  font-weight: 600;
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.15) 0%, rgba(152, 236, 45, 0.15) 100%);
  border: 1px solid rgba(23, 173, 55, 0.2);
  color: #17ad37;
  animation: currentBreadcrumbGlow 2s ease-in-out infinite alternate;
}

@keyframes currentBreadcrumbGlow {
  from {
    box-shadow: 0 2px 8px rgba(23, 173, 55, 0.2);
  }
  to {
    box-shadow: 0 4px 16px rgba(23, 173, 55, 0.4);
  }
}

.breadcrumb-icon {
  margin-right: 0.5rem;
  font-size: 14px;
  transition: transform 0.3s ease;
}

.breadcrumb-link:hover .breadcrumb-icon {
  transform: scale(1.1) rotate(5deg);
}

.breadcrumb-text {
  white-space: nowrap;
  position: relative;
}

/* Mobile breadcrumb responsiveness */
@media (max-width: 991px) {
  .breadcrumb-container {
    display: none !important;
  }
}

@media (min-width: 992px) and (max-width: 1199px) {
  .breadcrumb-text {
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* Breadcrumb hover effects */
.breadcrumb-item {
  position: relative;
}

.breadcrumb-item::before {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 0.5rem;
  border-radius: 0.375rem;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 1000;
}

.breadcrumb-item:hover::before {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(-8px);
}


/* Focus and Accessibility Improvements */
.nav-link:focus,
.breadcrumb-link:focus,
.notification-item:focus,
.dropdown-item:focus {
  outline: 2px solid #17ad37;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px rgba(23, 173, 55, 0.2);
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .nav-link,
  .breadcrumb-link,
  .dropdown-menu {
    border-width: 2px;
    border-color: currentColor;
  }
  
  .notification-badge {
    border: 2px solid white;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  *,
  ::before,
  ::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
  
  .avatar {
    animation: none;
  }
  
  .notification-badge {
    animation: none;
  }
}

/* Screen reader only content */
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Skip Links Container */
.skip-links {
  position: fixed;
  top: -200px;
  left: 0;
  right: 0;
  z-index: 10001;
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  transition: top 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.skip-links:focus-within {
  top: 0;
}

/* Skip link individual */
.skip-link {
  background: linear-gradient(135deg, #17ad37 0%, #98ec2d 100%);
  color: white;
  padding: 0.75rem 1rem;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  border: 2px solid transparent;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 15px rgba(23, 173, 55, 0.3);
  position: relative;
  overflow: hidden;
}

.skip-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0) 0%, 
    rgba(255, 255, 255, 0.2) 50%, 
    rgba(255, 255, 255, 0) 100%);
  transition: left 0.5s ease;
}

.skip-link:hover::before,
.skip-link:focus::before {
  left: 100%;
}

.skip-link:hover,
.skip-link:focus {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 25px rgba(23, 173, 55, 0.4);
  border-color: rgba(255, 255, 255, 0.3);
  outline: none;
}

.skip-link:active {
  transform: translateY(0) scale(0.98);
  transition-duration: 0.1s;
}

/* Animación de entrada para los skip links */
.skip-link:nth-child(1) { animation-delay: 0.1s; }
.skip-link:nth-child(2) { animation-delay: 0.2s; }
.skip-link:nth-child(3) { animation-delay: 0.3s; }
.skip-link:nth-child(4) { animation-delay: 0.4s; }

.skip-links:focus-within .skip-link {
  animation: skipLinkSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes skipLinkSlideIn {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Live regions - hidden visually but announced to screen readers */
#accessibility-announcements,
#accessibility-alerts {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Keyboard Shortcuts Modal */
.shortcuts-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 10010;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: modalOverlayIn 0.3s ease;
}

@keyframes modalOverlayIn {
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(8px);
  }
}

.shortcuts-modal {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(23, 173, 55, 0.2);
  border-radius: 1.5rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  outline: none;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.shortcuts-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(23, 173, 55, 0.1);
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.05) 0%, rgba(152, 236, 45, 0.05) 100%);
}

.shortcuts-modal-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--dm-text-primary, #1f2937);
  display: flex;
  align-items: center;
}

.shortcuts-modal-title i {
  color: var(--dm-accent-primary, #17ad37);
  margin-right: 0.5rem;
}

.shortcuts-modal-close {
  background: none;
  border: none;
  color: var(--dm-text-secondary, #6b7280);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.shortcuts-modal-close:hover {
  color: var(--dm-accent-primary, #17ad37);
  background: rgba(23, 173, 55, 0.1);
  transform: scale(1.1);
}

.shortcuts-modal-body {
  padding: 2rem;
  max-height: 60vh;
  overflow-y: auto;
}

.shortcuts-section {
  margin-bottom: 2rem;
}

.shortcuts-section:last-child {
  margin-bottom: 0;
}

.shortcuts-section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--dm-text-primary, #1f2937);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(23, 173, 55, 0.1);
}

.shortcuts-section-title i {
  color: var(--dm-accent-primary, #17ad37);
  margin-right: 0.5rem;
}

.shortcuts-grid {
  display: grid;
  gap: 0.75rem;
}

.shortcut-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: rgba(248, 250, 252, 0.8);
  border: 1px solid rgba(23, 173, 55, 0.1);
  border-radius: 0.75rem;
  transition: all 0.3s ease;
}

.shortcut-item:hover {
  background: rgba(23, 173, 55, 0.05);
  border-color: rgba(23, 173, 55, 0.2);
  transform: translateX(4px);
}

.shortcut-keys {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.shortcut-keys kbd {
  background: linear-gradient(135deg, var(--dm-accent-primary, #17ad37) 0%, var(--dm-accent-secondary, #98ec2d) 100%);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 4px rgba(23, 173, 55, 0.2);
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
}

.shortcut-description {
  color: var(--dm-text-secondary, #6b7280);
  font-size: 0.875rem;
  font-weight: 500;
}

.shortcuts-modal-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(23, 173, 55, 0.1);
  background: rgba(248, 250, 252, 0.5);
  display: flex;
  justify-content: center;
}

.shortcuts-modal-footer .btn {
  padding: 0.75rem 2rem;
  border-radius: 0.75rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--dm-accent-primary, #17ad37) 0%, var(--dm-accent-secondary, #98ec2d) 100%);
  border: none;
  color: white;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 15px rgba(23, 173, 55, 0.3);
}

.shortcuts-modal-footer .btn:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 25px rgba(23, 173, 55, 0.4);
}

/* Scrollbar personalizado para el modal */
.shortcuts-modal-body::-webkit-scrollbar {
  width: 6px;
}

.shortcuts-modal-body::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.shortcuts-modal-body::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, var(--dm-accent-primary, #17ad37), var(--dm-accent-secondary, #98ec2d));
  border-radius: 3px;
}

.shortcuts-modal-body::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to bottom, #15a832, #8dd129);
}

/* Responsive para móviles */
@media (max-width: 768px) {
  .shortcuts-modal {
    max-width: 95%;
    margin: 1rem;
  }
  
  .shortcuts-modal-header,
  .shortcuts-modal-footer {
    padding: 1rem 1.5rem;
  }
  
  .shortcuts-modal-body {
    padding: 1.5rem;
  }
  
  .shortcuts-modal-title {
    font-size: 1.25rem;
  }
  
  .shortcut-item {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
  
  .shortcuts-grid {
    gap: 0.5rem;
  }
}

/* Dark mode support */
body.dark-mode-active .shortcuts-modal {
  background: rgba(17, 24, 39, 0.98);
  border-color: rgba(55, 65, 81, 0.3);
}

body.dark-mode-active .shortcuts-modal-header {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  border-bottom-color: rgba(55, 65, 81, 0.3);
}

body.dark-mode-active .shortcuts-modal-footer {
  background: rgba(31, 41, 55, 0.5);
  border-top-color: rgba(55, 65, 81, 0.3);
}

body.dark-mode-active .shortcut-item {
  background: rgba(31, 41, 55, 0.8);
  border-color: rgba(55, 65, 81, 0.3);
}

body.dark-mode-active .shortcut-item:hover {
  background: rgba(23, 173, 55, 0.1);
  border-color: rgba(23, 173, 55, 0.3);
}

/* Enhanced keyboard navigation styles */
.nav-item:focus-within .nav-link {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(23, 173, 55, 0.15);
}

/* Color blind friendly indicators */
.notification-badge {
  position: relative;
}

/* Enhanced Dark Mode Transition */
:global(body) {
  transition: background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1), 
              color 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Responsive */
@media (max-width: 768px) {
  .fs-26 {
    font-size: 22px !important;
  }
  
  .nav-item {
    margin: 0 0.25rem;
  }
}

/* Navbar Blur Effect - Glassmorphism Override */
.navbar.blur {
  backdrop-filter: blur(25px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(25px) saturate(200%) !important;
  background: rgba(255, 255, 255, 0.15) !important;
  background-color: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
}




/* Moderately larger navbar icons for better visibility */
.navbar-icon-large {
  font-size: 1.25rem !important; /* 20px - moderately larger */
  min-width: 1.5rem;
  min-height: 1.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}


/* Fixed Plugin (Configurator) Z-Index Fix */
:global(.fixed-plugin .card) {
  z-index: 1035 !important;
}

:global(.fixed-plugin.show .card) {
  z-index: 1035 !important;
}

/* Disabled Notifications Button */
.disabled-notifications-btn {
  cursor: default !important;
  pointer-events: auto !important;
  opacity: 0.7;
  position: relative;
}

.disabled-notifications-btn:hover {
  transform: none !important;
  box-shadow: none !important;
  background: #6c757d !important;
  border-color: #6c757d !important;
}

.disabled-notifications-btn::after {
  content: 'Próximamente';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 10px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  pointer-events: none;
  z-index: 1000;
}

.disabled-notifications-btn:hover::after {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(-5px);
}

/* ================================
   BREADCRUMBS INTELIGENTES
   ================================ */

/* Contenedor principal de breadcrumbs */
.breadcrumb-container {
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.breadcrumb-container.loading {
  opacity: 0.7;
}

/* Skeleton loader para breadcrumbs */
.breadcrumb-skeleton {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
}

.skeleton-item {
  height: 1.25rem;
  background: linear-gradient(90deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.15) 50%, rgba(23, 173, 55, 0.1) 100%);
  border-radius: 0.375rem;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-item:nth-child(1) { width: 4rem; }
.skeleton-item:nth-child(3) { width: 6rem; }
.skeleton-item:nth-child(5) { width: 8rem; }

.skeleton-separator {
  width: 0.75rem;
  height: 0.75rem;
  background: rgba(23, 173, 55, 0.2);
  border-radius: 50%;
  animation: skeleton-pulse 1.5s ease-in-out infinite 0.2s;
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
}

/* Lista de breadcrumbs mejorada */
.animated-breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0;
  margin: 0;
  list-style: none;
  flex-wrap: wrap;
}

/* Items de breadcrumb */
.breadcrumb-item {
  display: flex;
  align-items: center;
  position: relative;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  animation: slideInFromLeft 0.4s ease-out;
  animation-fill-mode: both;
}

.breadcrumb-item:nth-child(1) { animation-delay: 0.1s; }
.breadcrumb-item:nth-child(2) { animation-delay: 0.15s; }
.breadcrumb-item:nth-child(3) { animation-delay: 0.2s; }
.breadcrumb-item:nth-child(4) { animation-delay: 0.25s; }
.breadcrumb-item:nth-child(n+5) { animation-delay: 0.3s; }

/* Estados especiales */
.breadcrumb-item.home {
  font-weight: 700;
}

.breadcrumb-item.id-segment {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 0.875rem;
}

.breadcrumb-item.uuid-segment {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 0.8125rem;
}

.breadcrumb-item.date-segment {
  font-style: italic;
  color: #6366f1;
}

.breadcrumb-item.generated {
  opacity: 0.8;
}

/* Separadores inteligentes */
.breadcrumb-separator {
  color: rgba(23, 173, 55, 0.5);
  font-size: 0.875rem;
  margin: 0 0.5rem;
  transition: all 0.3s ease;
}

.breadcrumb-item:hover + .breadcrumb-item .breadcrumb-separator,
.breadcrumb-item:hover .breadcrumb-separator {
  color: #17ad37;
  transform: scale(1.2);
}

/* Links navegables mejorados */
.breadcrumb-link {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  text-decoration: none;
  color: inherit;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: transparent;
  border: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.breadcrumb-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(23, 173, 55, 0.1), transparent);
  transition: left 0.5s ease;
}

.breadcrumb-link:hover::before {
  left: 100%;
}

.breadcrumb-link:hover {
  color: #17ad37;
  background: rgba(23, 173, 55, 0.05);
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 4px 12px rgba(23, 173, 55, 0.15);
}

.breadcrumb-link:active {
  transform: translateY(0) scale(0.98);
}

/* Iconos de breadcrumb */
.breadcrumb-icon {
  font-size: 1rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.breadcrumb-link:hover .breadcrumb-icon {
  transform: scale(1.1) rotate(5deg);
  color: #17ad37;
}

/* Texto de breadcrumb */
.breadcrumb-text {
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.025em;
  transition: all 0.3s ease;
  line-height: 1.2;
}

.breadcrumb-link:hover .breadcrumb-text {
  font-weight: 700;
}

/* Badges especiales */
.breadcrumb-badge {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.125rem 0.375rem;
  border-radius: 0.375rem;
  margin-left: 0.375rem;
  line-height: 1;
  transition: all 0.3s ease;
}

.id-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.uuid-badge {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
}

.date-badge {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
}

/* Elemento actual (breadcrumb activo) */
.breadcrumb-current {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  border: 1px solid rgba(23, 173, 55, 0.2);
  backdrop-filter: blur(10px);
  position: relative;
  font-weight: 700;
  color: #17ad37;
}

.current-icon {
  color: #17ad37;
  animation: currentIconGlow 2s ease-in-out infinite;
}

.current-text {
  color: #17ad37;
  font-weight: 700;
}

.current-indicator {
  margin-left: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #17ad37;
  color: white;
  border-radius: 50%;
  font-size: 0.75rem;
  animation: currentPulse 2s ease-in-out infinite;
  flex-shrink: 0;
  line-height: 1;
}

.current-indicator i {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transform: translateY(-1px);
}

@keyframes currentIconGlow {
  0%, 100% { 
    transform: scale(1);
    filter: drop-shadow(0 0 4px rgba(23, 173, 55, 0.3));
  }
  50% { 
    transform: scale(1.1);
    filter: drop-shadow(0 0 8px rgba(23, 173, 55, 0.5));
  }
}

@keyframes currentPulse {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 0 rgba(23, 173, 55, 0.4);
  }
  50% { 
    transform: scale(1.1);
    box-shadow: 0 0 0 4px rgba(23, 173, 55, 0.2);
  }
}

/* Botón móvil para breadcrumbs */
.breadcrumb-mobile-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.1) 0%, rgba(152, 236, 45, 0.1) 100%);
  border: 1px solid rgba(23, 173, 55, 0.2);
  color: #17ad37;
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.breadcrumb-mobile-toggle:hover {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.15) 0%, rgba(152, 236, 45, 0.15) 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(23, 173, 55, 0.15);
}

/* Modal de breadcrumbs móviles */
.mobile-breadcrumbs {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  animation: fadeIn 0.3s ease;
}

.mobile-breadcrumb-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(23, 173, 55, 0.1);
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.05) 0%, rgba(152, 236, 45, 0.05) 100%);
}

.mobile-breadcrumb-header h6 {
  margin: 0;
  font-weight: 700;
  color: #17ad37;
}

.mobile-breadcrumb-header button {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  transition: all 0.3s ease;
}

.mobile-breadcrumb-header button:hover {
  color: #17ad37;
  background: rgba(23, 173, 55, 0.1);
}

.mobile-breadcrumb-list {
  max-height: 60vh;
  overflow-y: auto;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 400px;
}

.mobile-breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
}

.mobile-breadcrumb-item:hover {
  background: rgba(23, 173, 55, 0.05);
  color: #17ad37;
}

.mobile-breadcrumb-item:last-child {
  border-bottom: none;
}

.mobile-breadcrumb-item .current-mark {
  margin-left: auto;
  color: #17ad37;
  font-size: 1.25rem;
}

/* Animaciones */
@keyframes slideInFromLeft {
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Responsive design */
@media (max-width: 1200px) {
  .breadcrumb-text {
    font-size: 0.8125rem;
  }
  
  .breadcrumb-icon {
    font-size: 0.9375rem;
  }
  
  .breadcrumb-badge {
    font-size: 0.5625rem;
    padding: 0.0625rem 0.25rem;
  }
}

@media (max-width: 992px) {
  .breadcrumb-text {
    font-size: 0.75rem;
  }
  
  .breadcrumb-icon {
    font-size: 0.875rem;
  }
  
  .breadcrumb-link,
  .breadcrumb-current {
    padding: 0.25rem 0.5rem;
    gap: 0.25rem;
  }
}

/* Estados de modo oscuro */
body.dark-mode-active .breadcrumb-current {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.2) 0%, rgba(152, 236, 45, 0.2) 100%);
  border-color: rgba(23, 173, 55, 0.3);
}

body.dark-mode-active .breadcrumb-link:hover {
  background: rgba(23, 173, 55, 0.15);
}

body.dark-mode-active .mobile-breadcrumb-list {
  background: #1f2937;
  color: white;
}

body.dark-mode-active .mobile-breadcrumb-header {
  background: linear-gradient(135deg, rgba(23, 173, 55, 0.15) 0%, rgba(152, 236, 45, 0.15) 100%);
  border-bottom-color: rgba(55, 65, 81, 0.3);
}

/* Accesibilidad */
@media (prefers-reduced-motion: reduce) {
  .breadcrumb-item,
  .breadcrumb-link,
  .breadcrumb-icon,
  .current-indicator {
    animation: none !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus states para accesibilidad */
.breadcrumb-link:focus,
.breadcrumb-mobile-toggle:focus {
  outline: 2px solid #17ad37;
  outline-offset: 2px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .breadcrumb-current {
    border-width: 2px;
  }
  
  .breadcrumb-link:hover {
    border: 2px solid #17ad37;
  }
  
  .breadcrumb-badge {
    border: 1px solid currentColor;
  }
}

</style>